import { SelectionModel } from '@angular/cdk/collections';
import { AfterViewInit, ChangeDetectorRef, Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { Router } from '@angular/router';
import { InfoDialogComponent } from '@klickdata/core/application/src/info-dialog/info-dialog.component';
import { MessageDeletedUndoComponent, MessageSavedComponent, MessageService } from '@klickdata/core/message';
import { MobileService } from '@klickdata/core/mobile';
import { Post } from '@klickdata/core/post';
import { AppScope, ResourceTag, ResourceTagData } from '@klickdata/core/resource';
import {
    Filter,
    GlobalFilterProperty,
    GlobalFilterPropertyType,
    SelectFilterOption,
    TableFilterComponent,
    TableSource,
} from '@klickdata/core/table';
import { ShareService } from '@klickdata/shared-components/src/share/share.service';
import { Observable, Subject } from 'rxjs';
import { filter, first, map, takeUntil, tap } from 'rxjs/operators';
import { DownloadHelperService } from 'apps/klickdata/src/app/shared/dialog/download-pdf-dialog/download-helper.service';
import { User } from '@klickdata/core/user';
import { AuthService } from '@klickdata/core/auth';
import { Utils } from '@klickdata/core/util';
import { PostListService } from 'apps/klickdata/src/app/admin/content/posts/post-list.service';
import { Customer } from '@klickdata/core/customer';
import { PostsAssignmentComponent } from '../../posts-assignment/posts-assignment.component';

@Component({
    selector: 'app-posts-listing-general',
    templateUrl: './posts-listing-general.component.html',
    styleUrls: ['./posts-listing-general.component.scss'],
    providers: [PostListService],
})
export class PostsListingGeneralComponent implements OnInit, AfterViewInit, OnDestroy {
    public posts: Observable<Post[]>;
    public isMobile: boolean;
    public loading = true;
    public allowActionRow = true;
    public showActionHeader = false;
    public dataSource = new TableSource<Post>();
    @Input() public columns = [
        'select',
        'columns',
        'title',
        'tags',
        'status',
        'created_at',
        'published_at',
        'language_id',
    ];
    public authUser: User;
    @Input() tableFilters: Filter<string | number>[];
    @Input() public pageSize = 25;
    @ViewChild(MatSort) sort: MatSort;
    @ViewChild(MatPaginator) paginator: MatPaginator;
    @ViewChild(TableFilterComponent) filter: TableFilterComponent;
    selection = new SelectionModel<Post>(true, []);
    public destroy$: Subject<boolean> = new Subject<boolean>();
    expandedRow: Post | null;
    AppScope = AppScope;
    GlobalFilterProperty = GlobalFilterProperty;
    GlobalFilterPropertyType = GlobalFilterPropertyType;
    public generalModelStatusSelectOptions: SelectFilterOption[];
    public customer$: Observable<Customer>;
    constructor(
        protected auth: AuthService,
        protected postService: PostListService,
        protected message: MessageService,
        protected router: Router,
        protected bottomSheet: MatBottomSheet,
        protected dialog: MatDialog,
        protected mobileService: MobileService,
        protected cdRef: ChangeDetectorRef,
        public shareService: ShareService,
        protected downloadHelper: DownloadHelperService
    ) {
        this.generalModelStatusSelectOptions = Utils.getGeneralModelStatusSelectOptions();
        this.customer$ = this.auth.getCustomer().pipe(first());
        this.auth
            .getUser()
            .pipe(takeUntil(this.destroy$))
            .subscribe((user) => {
                this.authUser = user;
            });
    }

    public ngOnInit(): void {
        this.mobileService
            .isMobile()
            .pipe(takeUntil(this.destroy$))
            .subscribe((isMobile) => (this.isMobile = isMobile));

        if (this.isMobile && this.columns.indexOf('tools') === -1) {
            this.columns.push('tools');
        }
        this.dataSource.service = this.postService;
    }
    public ngAfterViewInit(): void {
        this.dataSource.sort = this.sort;
        this.dataSource.paginator = this.paginator;
        this.dataSource.filter = this.filter;

        this.selection.changed.pipe(takeUntil(this.destroy$)).subscribe((values) => {
            this.showActionHeader = values.source.selected.length > 0;
            this.cdRef.markForCheck();
        });
        if (this.tableFilters) {
            this.filter.createOrUpdateWithoutEmitFilter(this.tableFilters);
        }
    }

    /**
     * Navigate to edit a post.
     */
    public edit(post: Post): void {
        this.router.navigate(['/admin/content/posts', post.id]);
    }
    public onTagClick(tag: ResourceTagData) {
        this.filter.filterByModel(new ResourceTag(tag).getFilter());
    }
    /**
     * Delete a user
     *
     * @param post Post
     */
    public deletePost(post: Post): void {
        this.postService.destroy(post).subscribe(() => {
            const ref = this.message.openMessage(MessageDeletedUndoComponent);
            ref.instance.snackBarRef = ref;
            this.dataSource.removeByIds([post.id]);
            this.selection.clear();
            // Check if user want to undo.
            ref.onAction().subscribe(() => {
                this.restorePost(post);
            });

            // this.loadPosts();
        });
    }
    public download(post: Post) {
        this.downloadHelper.download(post.downloads);
    }
    protected loadPosts(): void {
        this.loading = true;
        this.posts = this.postService.getPublicPosts().pipe(
            map((res) => {
                this.loading = false;
                return res;
            })
        );
    }

    /**
     * Restore a removed post
     */
    protected restorePost(user: Post) {
        this.postService.restore(user.id).subscribe(() => {
            this.message.openMessage(MessageSavedComponent);
            this.loadPosts();
        });
    }

    public onRowClick(ev: Event, group: Post) {
        ev.stopPropagation();
        if (!this.isMobile) {
            this.edit(group);
        }
    }
    /** Whether the number of selected elements matches the total number of rows. */
    isAllSelected() {
        const numSelected = this.selection.selected.length;
        const numRows = this.dataSource.data.length;
        return numSelected === numRows;
    }

    /** Selects all rows if they are not all selected; otherwise clear selection. */
    masterToggle() {
        this.isAllSelected()
            ? this.selection.clear()
            : this.dataSource.data.forEach((row) => this.selection.select(row));
    }

    /** The label for the checkbox on the passed row */
    checkboxLabel(row?: Post): string {
        if (!row) {
            return `${this.isAllSelected() ? 'select' : 'deselect'} all`;
        }
        return `${this.selection.isSelected(row) ? 'deselect' : 'select'} row ${row.id + 1}`;
    }

    public functionNotImpl() {
        this.dialog.open(InfoDialogComponent, {
            disableClose: false,
            data: {
                contentBody: $localize`:@@thisFunctionWillSoonWorkGreat! :This function will soon work great! `,
                neutralBtn: $localize`:@@ok:Ok`,
            },
        });
    }

    public onVisibilityToggle(post: Post) {
        this.postService
            .update({ id: post.id, visibility: post.visibility })
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => {
                this.message.openMessage(MessageSavedComponent);
            });
    }
    specify(customer: Customer, post: Post) {
        this.bottomSheet
            .open(PostsAssignmentComponent, {
                data: {
                    context: 'publish',
                    contextTitle: $localize`:@@specifyPublish:Specify Publish`,
                    contextIcon: 'publish_outline',
                    title: post.title,
                    users_access_open: post.public,
                    post: post,
                    customer: customer,
                },
                panelClass: 'sheet-wrapper',
            })
            .afterDismissed()
            .pipe(
                takeUntil(this.destroy$),
                filter((result) => !!result)
            )
            .subscribe((value) => {
                this.message.openMessage(MessageSavedComponent);
            });
    }

    ngOnDestroy() {
        this.destroy$.next(true);
        this.destroy$.unsubscribe();
    }
}
